/** Build a standalone HTML report for a saved analysis and open it for print. */
import type { SavedAnalysis } from "../types";
import { fmtNum, fmtP } from "./format";

export function analysisToHtml(a: SavedAnalysis): string {
  const r = a.result;
  const rolesList = Object.entries(a.roles)
    .map(([k, v]) => `<li><b>${esc(k)}</b>: ${esc(Array.isArray(v) ? v.join(", ") : String(v))}</li>`)
    .join("");
  const tables = r.tables
    .map(
      (t) => `<h3>${esc(t.title)}</h3><table><thead><tr>${t.columns
        .map((c) => `<th>${esc(c || " ")}</th>`)
        .join("")}</tr></thead><tbody>${t.rows
        .map(
          (row) =>
            `<tr>${row
              .map((c) => `<td>${c === null ? "—" : typeof c === "number" ? fmtNum(c) : esc(String(c))}</td>`)
              .join("")}</tr>`,
        )
        .join("")}</tbody></table>`,
    )
    .join("");
  const assumptions = r.assumptions
    .map((x) => `<li>${x.passed === false ? "⚠ " : x.passed === true ? "✓ " : "• "}<b>${esc(x.name)}</b> — ${esc(x.detail)}</li>`)
    .join("");
  const effects = r.effectSizes
    .map((e) => `<li><b>${esc(e.name)}</b>: ${fmtNum(e.value)}${e.magnitude ? ` (${esc(e.magnitude)})` : ""}</li>`)
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(r.testName)} — ${esc(a.datasetName)}</title>
<style>
body{font:14px/1.6 -apple-system,Segoe UI,Roboto,sans-serif;max-width:760px;margin:2rem auto;padding:0 1rem;color:#0f172a}
h1{font-size:1.4rem;margin-bottom:0} h2,h3{margin-top:1.6rem}
.muted{color:#64748b} table{border-collapse:collapse;width:100%;margin:.5rem 0}
th,td{border:1px solid #cbd5e1;padding:.35rem .5rem;text-align:left;font-size:13px}
code{background:#f1f5f9;padding:.1rem .3rem;border-radius:4px}
@media print{body{margin:0}}
</style></head><body>
<h1>${esc(r.testName)}</h1>
<p class="muted">Dataset: <b>${esc(a.datasetName)}</b> · ${new Date(a.createdAt).toLocaleString()}</p>
<p>${esc(r.summary)}</p>
${r.apa ? `<p><b>APA:</b> ${esc(r.apa)}</p>` : ""}
<p><b>${r.pValue !== null ? fmtP(r.pValue) : ""}</b></p>
<h3>Variables</h3><ul>${rolesList}</ul>
${effects ? `<h3>Effect size</h3><ul>${effects}</ul>` : ""}
${assumptions ? `<h3>Assumptions</h3><ul>${assumptions}</ul>` : ""}
${tables}
${r.notes.length ? `<h3>Notes</h3><ul>${r.notes.map((n) => `<li>${esc(n)}</li>`).join("")}</ul>` : ""}
<p class="muted">Generated by Stats Analysis — computed locally in the browser.</p>
</body></html>`;
}

export function openReport(a: SavedAnalysis): void {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(analysisToHtml(a));
  w.document.close();
}

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => HTML_ESCAPES[c] ?? c);
}
